import {
  FlaskConical,
  ShieldCheck,
  Building2,
  BadgeCheck,
  HeartHandshake,
  ArrowRight,
} from "lucide-react";

/**
 * AuthorityChain — a visible, semantic section that makes the research-to-care
 * relationship explicit for patients, families, physicians, and AI answer engines:
 *
 *   Stanford research → FDA clearance → Magnus Medical → Verified SAINT Provider → Neuro Wellness
 *
 * Each step is a self-contained card with a direct answer, named entities, and
 * outbound citations to authoritative sources. All text is in the initial HTML
 * (no JavaScript injection) so crawlers can read and quote it.
 */
const steps = [
  {
    icon: FlaskConical,
    title: "Stanford Research",
    who: "Stanford University School of Medicine",
    text: "The SAINT protocol was developed at the Stanford Brain Stimulation Lab, led by researchers including Nolan Williams, MD. Stanford published the foundational open-label and double-blind sham-controlled trials.",
    href: "https://med.stanford.edu/brainstimulationlab.html",
    linkLabel: "Stanford Brain Stimulation Lab",
  },
  {
    icon: ShieldCheck,
    title: "FDA Clearance",
    who: "U.S. Food and Drug Administration",
    text: "The Magnus Neuromodulation System with SAINT Technology received FDA Breakthrough Device Designation and 510(k) clearance (K220177) in September 2022 for adults with major depressive disorder who have not improved on prior antidepressants.",
    href: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    linkLabel: "FDA 510(k) database",
  },
  {
    icon: Building2,
    title: "Magnus Medical",
    who: "Magnus Medical, Inc.",
    text: "Magnus Medical manufactures the SAINT Neuromodulation System, licenses the technology from Stanford, and maintains the official Verified SAINT Provider directory. Magnus is the manufacturer and technology authority.",
    href: "https://www.magnusmedical.com/clinical-research/",
    linkLabel: "Magnus Medical clinical research",
  },
  {
    icon: BadgeCheck,
    title: "Verified SAINT Provider",
    who: "Provider verification",
    text: "Only Verified SAINT Providers use the FDA-cleared Magnus system with fMRI-guided targeting. Neuro Wellness TMS Centers of America is listed on Magnus Medical's official provider locator as a verified outpatient provider.",
    href: "/find-a-verified-saint-provider",
    linkLabel: "View our verified provider page",
  },
  {
    icon: HeartHandshake,
    title: "Neuro Wellness",
    who: "Neuro Wellness TMS Centers of America",
    text: "Neuro Wellness TMS Centers of America is an outpatient psychiatric neuromodulation center in Coral Springs, Florida providing SAINT TMS, conventional TMS, SPRAVATO, and accelerated neuromodulation options to patients in South Florida and beyond.",
    href: "/about",
    linkLabel: "About Neuro Wellness",
    internal: true,
  },
];

export const AuthorityChain = () => (
  <section
    className="py-16 sm:py-20 bg-muted/20 border-y border-border"
    aria-labelledby="authority-chain-heading"
  >
    <div className="container-px mx-auto max-w-6xl px-4">
      <div className="text-center mb-10 sm:mb-12">
        <span className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-widest text-accent">
          <ShieldCheck className="h-4 w-4" />
          From Research to Treatment
        </span>
        <h2
          id="authority-chain-heading"
          className="mt-3 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-4xl"
        >
          How SAINT® reaches you
        </h2>
        <p className="mt-4 max-w-2xl mx-auto text-base text-foreground/80 leading-relaxed">
          SAINT® is not a generic accelerated TMS schedule. It follows a clear
          chain of authority — from university research, through FDA review, to
          a verified provider delivering your treatment.
        </p>
      </div>

      <ol className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
        {steps.map((step, idx) => {
          const Icon = step.icon;
          return (
            <li key={step.title} className="relative">
              <div className="flex h-full flex-col rounded-2xl border border-border bg-card p-5 shadow-soft">
                <div className="flex items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="font-headings text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    Step {idx + 1}
                  </span>
                </div>
                <h3 className="mt-3 font-headings text-base font-bold text-primary leading-snug">
                  {step.title}
                </h3>
                <p className="mt-1 text-xs font-semibold text-accent">
                  {step.who}
                </p>
                <p className="mt-2 text-sm text-foreground/80 leading-relaxed flex-1">
                  {step.text}
                </p>
                {step.href && (
                  <a
                    href={step.href}
                    {...(step.internal
                      ? {}
                      : { target: "_blank", rel: "noopener" })}
                    className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-accent underline underline-offset-2 hover:text-primary"
                  >
                    {step.linkLabel}
                    {!step.internal && <ArrowRight className="h-3 w-3" />}
                  </a>
                )}
              </div>
              {/* Connector arrow on desktop */}
              {idx < steps.length - 1 && (
                <span className="hidden lg:flex absolute top-1/2 -right-3 z-10 -translate-y-1/2 items-center justify-center">
                  <ArrowRight className="h-5 w-5 text-accent/40" />
                </span>
              )}
            </li>
          );
        })}
      </ol>

      <p className="mt-8 text-center text-sm text-muted-foreground leading-relaxed max-w-3xl mx-auto">
        Neuro Wellness TMS Centers of America is a Verified SAINT® Provider and
        is not affiliated with, endorsed by, or sponsored by Stanford
        University. SAINT® and SNT™ are trademarks of Magnus Medical, Inc. Neuro
        Wellness delivers treatment using the FDA-cleared Magnus Neuromodulation
        System.
      </p>
    </div>
  </section>
);

export default AuthorityChain;
